"use client";

import { FC, useEffect, useState } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { motion } from "framer-motion";
import { PrismicNextImage } from "@prismicio/next";
import Image from "next/image";
import distinctiveEdgeImageBg from "@/assets/distinctiveEdgeImageBg.svg";
import distinctiveEdgeImageMobileBg from "@/assets/distinctiveEdgeImageBgMobile.svg";
import { useMediaQuery } from "usehooks-ts";

/**
 * Props for `OurDistinctiveEdgeSection`.
 */
export type OurDistinctiveEdgeSectionProps =
  SliceComponentProps<Content.OurDistinctiveEdgeSectionSlice>;

/**
 * Component for "OurDistinctiveEdgeSection" Slices.
 */
const OurDistinctiveEdgeSection: FC<OurDistinctiveEdgeSectionProps> = ({
  slice,
}) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const isLargerDevice = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted]);

  if (!mounted) {
    return;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#FAFDFF] overflow-hidden"
    >
      <Bounded as={"div"} className="md:py-[120px] py-[60px]">
        <div className="flex flex-col xl:flex-row items-center xl:items-start justify-center gap-[53px]">
          <motion.div
            className="flex-1 w-full xl:max-w-[586px]"
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="md:text-5xl text-4xl text-primary text-archivo leading-16 md:font-semibold font-normal mb-6">
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  label: ({ node, children }) => {
                    if (node.data.label === "Highlight") {
                      return <span className="text-secondary">{children}</span>;
                    }
                    return <span className="text-primary">{children}</span>;
                  },
                }}
              />
            </div>
            <div className="font-merriweather text-primary tracking-wide text-xl leading-7 font-normal mb-9">
              <PrismicRichText
                field={slice.primary.sub_title}
                components={{
                  label: ({ node, children }) => {
                    if (node.data.label === "Highlight") {
                      return <span className="font-bold">{children}</span>;
                    }
                    return <span className="font-normal">{children}</span>;
                  },
                }}
              />
            </div>
            <div className="space-y-4">
              {slice.primary.features.map((feature, idx) => (
                <div
                  key={feature.title}
                    className="px-4 py-6 flex items-start gap-4 justify-between border border-[#6AC1F7] rounded-3xl bg-[#FAFDFF] hover:bg-white hover:[box-shadow:0_5px_10px_0_rgba(3,68,109,0.06)]"
                >
                  <div className="h-[54px] w-[54px] flex items-center justify-center rounded-[10.8px] bg-[#E6F5FE]">
                    <PrismicNextImage field={feature.icon} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="text-[#033D61] font-archivo text-2xl font-medium leading-9">
                      {feature.title}
                    </h3>
                    <p className="font-merriweather text-[#033D61] text-sm font-normal leading-6">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true, amount: 0.5 }}
          >
            <div
              className="relative overflow-hidden w-[316px] h-[368px] md:w-[575px] md:h-[670px]"
              style={{
                clipPath: isLargerDevice
                  ? "path('M521.256 0V139.586C521.256 139.586 411.486 182.412 411.462 182.412C468.916 160.006 575.811 158.612 573.855 244.665C573.596 256.482 570.674 267.236 563.934 276.95C561.719 281.393 519.583 327.008 436.961 323.108C570.603 320.319 574.492 318.098 574.492 318.098V446.008L425.885 493.844C425.885 493.844 497.407 489.685 568.105 518.046C557.312 665.573 557.289 669.024 557.289 669.024C557.289 663.966 498.633 648.225 494.014 646.807C443.253 631.09 391.172 619.178 338.478 612.253C295.376 606.605 251.826 606.274 208.441 607.597C174.318 608.637 139.864 617.642 105.528 621.211C71.1928 624.78 48.9701 618.162 32.5446 587.035C18.6643 560.754 16.4726 527.074 39.3788 504.598C61.4601 482.925 94.6646 482.547 123.085 476.331C107.084 472.88 90.1399 468.626 78.9932 456.62C62.5206 438.894 63.2747 410.627 64.3352 388.032C64.7593 379.122 63.4868 364.776 67.0924 356.575C68.1057 354.235 79.1346 340.48 81.5148 340.361H78.2674C35.0416 340.361 5.10216e-05 305.32 5.10216e-05 262.094C5.10216e-05 221.743 30.6772 188.005 70.8467 184.179L146.994 176.927C146.994 176.927 75.7175 170.169 69.0484 116.778C69.0484 96.7599 77.3671 48.38 140.571 50.0344C150.516 50.2944 159.872 55.3285 169.392 57.692C214.498 68.8711 259.956 61.8989 305.627 59.6773C382.335 55.9194 454.093 37.7917 521.233 0L521.256 0Z')"
                  : "path('M287.249 0V76.9217C287.249 76.9217 226.758 100.522 226.745 100.522C258.406 88.1747 317.313 87.4063 316.235 134.828C316.092 141.34 314.482 147.266 310.768 152.619C309.547 155.068 286.327 180.205 240.796 178.056C314.443 176.519 316.586 175.295 316.586 175.295V245.782L234.693 272.143C234.693 272.143 274.107 269.851 313.067 285.48C307.119 366.778 307.106 368.68 307.106 368.68C307.106 365.893 274.782 357.218 272.237 356.437C244.264 347.776 215.564 341.211 186.526 337.395C162.773 334.283 138.774 334.1 114.866 334.83C96.0615 335.403 77.0751 340.365 58.1537 342.332C39.2323 344.298 26.986 340.651 17.9344 323.498C10.2853 309.015 9.07759 290.456 21.7005 278.07C33.8689 266.126 52.1669 265.918 67.8287 262.492C59.0108 260.591 49.6735 258.246 43.5309 251.63C34.4533 241.862 34.8689 226.285 35.4533 213.834C35.687 208.923 34.9857 201.018 36.9727 196.498C37.5311 195.209 43.6088 187.629 44.9205 187.563H43.1309C19.3104 187.563 2.81165e-05 168.253 2.81165e-05 144.433C2.81165e-05 122.196 16.9053 103.604 39.0416 101.496L81.0042 97.4993C81.0042 97.4993 41.7258 93.7752 38.0506 64.3532C38.0506 53.3216 42.6348 26.6608 77.4647 27.5725C82.9451 27.7158 88.1007 30.49 93.3473 31.7924C118.204 37.9529 143.255 34.1107 168.422 32.8864C210.694 30.8156 250.238 20.8259 287.236 0L287.249 0Z')",
              }}
            >
              <Image
                src={
                  isLargerDevice
                    ? distinctiveEdgeImageBg.src
                    : distinctiveEdgeImageMobileBg.src
                }
                alt="about section bg image"
                fill
              />
              <PrismicNextImage
                className="object-cover object-bottom"
                field={slice.primary.right_side_image}
                fill
              />
            </div>
            <div className="absolute inset-0 -top-5">
              <div className="relative w-full h-full">
                <div
                  className="w-full h-[292px] overflow-hidden"
                  style={{ clipPath: "inset(0 0 50%)" }}
                >
                  <PrismicNextImage
                    className="object-cover object-bottom"
                    field={slice.primary.right_side_image}
                    fill
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Bounded>
    </section>
  );
};

export default OurDistinctiveEdgeSection;
